import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { centsToPesos } from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Modal, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';
import { StatusBadge, type TransactionDto } from './TransactionsPage';
import { MonitoringDetailModal, type TransactionDetailDto } from './MonitoringPage';

/**
 * Phase 2D — Pick-up navigation view.
 *
 * Pick-up: the operational release queue for READY orders — search, branch
 * scope (admin filter), payment state, ready time, item-discrepancy flags
 * and a review detail that carries the release panel + confirmation.
 * (The Phase 2F delivery workflow lives in DeliveryPage.tsx.)
 *
 * Server-side: `status=READY&fulfillment=PICKUP&branch=…&q=…` — scope and
 * state are enforced by the API; this page only renders what it gets.
 */
const COPY = {
  title: 'Pick-up Transactions',
  description: 'Orders waiting for customer handoff — ready, reviewed, then released.',
  empty: 'No transactions are ready for pickup right now.',
  emptyHint: 'Transactions appear here automatically once they are marked Ready.',
};

function PaymentBadge({ status }: { status: TransactionDto['paymentStatus'] }) {
  return (
    <Badge tone={status === 'PAID' ? 'emerald' : status === 'PARTIAL' ? 'amber' : 'rose'}>
      {status}
    </Badge>
  );
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

export function PickupPage() {
  const { branchFilter } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState<TransactionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Debounced search by number/customer (same as Monitoring).
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  // Detail with the release panel (Phase 2D).
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TransactionDetailDto | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('status', 'READY');
    params.set('fulfillment', 'PICKUP');
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (query) params.set('q', query);

    void api
      .get<{ items: TransactionDto[] }>(`/transactions?${params.toString()}`)
      .then((res) => {
        if (!cancelled) return;
        setItems(res.items);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load transactions.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, query, refreshKey]);

  // Detail (facts + processing history + release panel) when a row is opened.
  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    void api
      .get<{ transaction: TransactionDetailDto }>(`/transactions/${detailId}`)
      .then((res) => {
        if (!cancelled) setDetail(res.transaction);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetailError(err instanceof ApiError ? err.message : 'Failed to load the transaction.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [detailId, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);
  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;

  return (
    <section className="space-y-4" aria-label={COPY.title}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{COPY.title}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · {COPY.description}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={refresh}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
          <Button variant="secondary" onClick={() => navigate('/admin/monitoring')}>
            <Icon name="monitoring" className="h-5 w-5" />
            Open Monitoring
          </Button>
        </div>
      </div>

      <Card className="flex items-start gap-3 p-4">
        <Icon name="shield" className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <p className="text-sm text-slate-500">
          Only <span className="font-semibold text-slate-700">READY</span> orders appear here;
          released orders leave the queue automatically. Release always asks for confirmation and
          is recorded in the transaction&rsquo;s history.
        </p>
      </Card>

      <Card className="p-4">
        <div className="relative">
          <Icon
            name="search"
            className="pointer-events-none absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search number or customer…"
            aria-label="Search ready transactions"
            className={`${inputCls} pl-10`}
          />
        </div>
      </Card>

      {error && <ErrorBanner message={error} />}

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-10 text-slate-500">
          <Spinner className="h-5 w-5 text-indigo-600" /> Loading transactions…
        </div>
      ) : items.length === 0 ? (
        <Card className="flex flex-col items-center p-8 text-center">
          <Icon name="pickup" className="h-8 w-8 text-slate-300" />
          <p className="mt-2 font-medium text-slate-700">
            {query ? 'No matching ready transactions' : COPY.empty}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {query ? 'Adjust the search to see more.' : COPY.emptyHint}
          </p>
        </Card>
      ) : (
        <>
          {/* Ready-for-pickup state: how many customers are waiting right now. */}
          <Card className="flex items-start gap-3 border-emerald-200 bg-emerald-50 p-4">
            <Icon name="pickup" className="mt-0.5 h-5 w-5 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-900">
                {items.length} {items.length === 1 ? 'order' : 'orders'} ready for pickup
              </p>
              <p className="text-sm text-emerald-700">
                Oldest received first. Open an order to review it, then release it to the customer.
              </p>
            </div>
          </Card>

          {/* Desktop table */}
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-3">Number</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Branch</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Received</th>
                  <th className="px-4 py-3">Ready</th>
                  <th className="px-4 py-3" aria-label="Actions" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((tx) => (
                  <tr
                    key={tx.id}
                    onClick={() => setDetailId(tx.id)}
                    className="cursor-pointer hover:bg-slate-100/70"
                  >
                    <td className="px-4 py-3 font-semibold text-slate-900">{tx.number}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{tx.customerName}</p>
                      <p className="text-xs text-slate-500">{tx.service.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge>{tx.branch.code}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-slate-600">{tx.itemCount}</span>
                      {tx.hasDiscrepancy && (
                        <Badge tone="rose" className="ml-2">
                          Δ count
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {centsToPesos(tx.totalCents)}
                    </td>
                    <td className="px-4 py-3">
                      <PaymentBadge status={tx.paymentStatus} />
                      {tx.paymentStatus !== 'PAID' && (
                        <p className="mt-0.5 text-xs font-semibold text-amber-700">
                          balance {centsToPesos(tx.totalCents - tx.amountPaidCents)}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={tx.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-500">{formatWhen(tx.createdAt)}</td>
                    <td className="px-4 py-3 text-slate-500">
                      {tx.readyAt ? formatWhen(tx.readyAt) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setDetailId(tx.id)}
                      >
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </>
      )}

      {/* Pick-up detail: facts, processing history, release panel + confirmation */}
      {detailId && !detail && !detailError && (
        <Modal title="Transaction" onClose={() => setDetailId(null)}>
          <div className="flex items-center justify-center gap-3 py-8 text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading transaction…
          </div>
        </Modal>
      )}
      {detailId && detailError && (
        <Modal title="Transaction" onClose={() => setDetailId(null)}>
          <ErrorBanner message={detailError} />
        </Modal>
      )}
      {detail && (
        <MonitoringDetailModal
          tx={detail}
          onClose={() => setDetailId(null)}
          onChanged={(next) => {
            setDetail(next);
            refresh();
          }}
        />
      )}
    </section>
  );
}
